import React, { useState } from 'react';

interface MfaVerifyProps {
  handleVerifyMfa: (code: string) => Promise<void>;
  error: string | null;
  isLoading: boolean;
  /** Abandon the challenge and return to sign-in */
  onCancel?: () => void;
}

const MfaVerify: React.FC<MfaVerifyProps> = ({
  handleVerifyMfa,
  error,
  isLoading,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setLocalError('Please enter the 6-digit code from your authenticator app');
      return;
    }
    setLocalError(null);
    void handleVerifyMfa(trimmed);
  };

  return (
    <div className="w-full max-w-md py-8 md:py-10">
      <h2 className="text-2xl font-semibold mb-1 text-slate-800">Two-Factor Authentication</h2>
      <p className="text-gray-500 mb-6 text-sm">Enter the verification code from your authenticator app</p>
      
      {(localError || error) && <div className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded mb-6 text-sm" role="alert">{localError || error}</div>}
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm" htmlFor="code">Verification Code</label>
          <input
            className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            type="text"
            id="code"
            name="code"
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
          />
        </div>
        <button 
          type="submit"
          className={`w-full py-3 px-4 bg-slate-900 text-white rounded-md text-sm font-medium transition ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-slate-800'}`}
          disabled={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </button>
      </form>
      {onCancel && (
        <div className="mt-6 text-center text-sm">
          <button type="button" onClick={onCancel} className="text-blue-600 hover:text-blue-700 font-medium">
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
};

export default MfaVerify;
